import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import { guessLanguage, setLanguage } from './i18n';
// Fonts are served by the app itself (no third-party font requests). Fira Sans covers Cyrillic, which Barlow lacks.
import '@fontsource/barlow/400.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';
import '@fontsource/barlow/700.css';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/fira-sans/400.css';
import '@fontsource/fira-sans/500.css';
import '@fontsource/fira-sans/600.css';
import '@fontsource/fira-sans/700.css';
import '@fontsource/fira-sans-condensed/600.css';
import '@fontsource/fira-sans-condensed/700.css';
import '@fontsource/jetbrains-mono/500.css';
import './styles.css';
import { initTheme } from './theme';
import { router } from './App';
import { Toaster } from './ui';
import { ApiError } from './api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2
    }
  }
});

// Any 401 from a data call means the session ended: drop cached data and go to sign-in
window.addEventListener('fg:unauthorized', () => {
  queryClient.clear();
  if (!/^\/(login|signup|forgot|reset)/.test(location.pathname)) void router.navigate('/login');
});

window.addEventListener('fg:mfa', () => { void queryClient.invalidateQueries({ queryKey: ['me'] }); });

initTheme();

// Sign-in pages use the device's last language (or the browser's) until the profile is known
void setLanguage(guessLanguage());

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  </StrictMode>
);
